import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import {
    CloseCircleOutlined,
    EditOutlined,
    LoginOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableMainContent from 'src/components/table-main-content';
import SimpleTableContent from 'src/components/simple-table-content';
import { Table, Tooltip } from 'antd';
import useTableHoverRow from 'src/hooks/use-table-hover-row';
import { useSelector } from 'react-redux';
import './style.scss';
import { noop } from 'lodash';

/**
 * Renders a table component to display a list of responsible data.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.data - An array of responsible objects to be displayed in the table.
 * @param {Function} props.patchResponsible - Callback function for updating responsible data.
 * @param {Boolean} props.edit - Function for editing data.
 * @returns {JSX.Element} The rendered table component.
 * @example
 *  <Table
        data={responsibles}
        patchResponsible={(id, status) => patchResponsible(id, status)}
        edit={(responsible) => setCreatemodal(responsible)}
    />
 */
function ResponsibleTable({ data, patchResponsible, edit }) {
    const { t } = useTranslation();
    
    /**
     * Set status for when it's active or not.
     * @param {string} activeStatus - "Y" or "N" values.
     * @returns {string} Text for status.
     */
    const normalizeActive = (activeStatus) => {
        if (activeStatus === "Y") {
            return t('enabled');
        }
        return t('disabled');
    }

        /**
     * Handles the keydown event and scrolls to the next actions div when the 'a' key is pressed.
     * @param {KeyboardEvent} event - The keydown event.
     */

    //TODO - Validate if that this shortcut keys are good to go
    const handleKeyDown = (event) => {
        if (event.key === 'F1') {
            const actionsDivs = Array.from(
                document.querySelectorAll('[id^="actions"]')
            );
            const currentIndex = actionsDivs.findIndex(
                (div) => div === document.activeElement
            );
            const nextIndex = currentIndex + 1;
            const nextActionsDiv = actionsDivs[nextIndex];
            if (nextActionsDiv) {
                nextActionsDiv.scrollIntoView({ behavior: 'smooth' });
                nextActionsDiv.focus();
            } else if (currentIndex === actionsDivs.length - 1) {
                actionsDivs[0].scrollIntoView({ behavior: 'smooth' });
                actionsDivs[0].focus();
            }
        }
    };

    /**
     * Adds an event listener for the keydown event and removes it when the component unmounts.
     */
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const [expandedRows] = useState([]);
    const { key: hoverKey, onMove, onLeave } = useTableHoverRow();
    const { sessionUser } = useSelector((store) => store.login);
    
    const isPDM = sessionUser && sessionUser.profile === 'PDM';
    
    const columnsConfig = [
        {
            title: t('name'),
            width: 160,
            dataIndex: 'name',
            fixed: 'left',
            sorter: (a, b) => {
                const nameA = a.name;
                const nameB = b.name;
                return nameA.localeCompare(nameB);
            },
            render: (text, { name, login }) => (
                <div className="collaborator-cell">
                    <TableMainContent 
                        mainContent={name}
                        mainContentAriaLabel={name}
                        translationMainContent={t('name')}
                        subContent={login}
                        subContentAriaLabel={login}
                        translationSubContent={t('collaboratorLogin')}
                    />
                </div>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            width: 180,
            sorter: (a, b) => a.email.localeCompare(b.email),
            render: (text) => {
                return (
                    <>
                        <SimpleTableContent 
                            mainContent={text}
                            mainContentAriaLabel={text}
                            translationMainContent={t('email')}
                        />
                    </>
                );
            },
        },
        {
            title: 'GU',
            dataIndex: 'gu',
            width: 120,
            sorter: (a, b) => a.gu.localeCompare(b.email),
            render: (text) => {
                return (
                    <>
                        <SimpleTableContent
                            mainContent={text}
                            mainContentAriaLabel={text}
                            translationMainContent={t('growthUnit')}
                        />
                    </>
                );
            },
        },
        {
            title: t('profile'),
            dataIndex: 'profile',
            width: 120,
            sorter: (a, b) => a.profile.localeCompare(b.profile),
            render: (text) => {
                return (
                    <>
                        <SimpleTableContent 
                            mainContent={text}
                            mainContentAriaLabel={text}
                            translationMainContent={t('profile')}
                        />
                    </>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'active',
            width: 120,
            render: (text) => {
                return (
                    <>
                        <SimpleTableContent 
                            mainContent={normalizeActive(text)} 
                            mainContentAriaLabel={normalizeActive(text)}
                            translationMainContent={t('status')}
                        />
                    </>
                );
            },
        },
        {
            title: t('actions'),
            dataIndex: '',
            fixed: 'right',
            width: 170,
            className: 'responsible-table__container_actions',
            render: (text, responsible) => {
                return (
                    <div 
                        id='actions'
                        onKeyDown={handleKeyDown( responsible)} 
                        tabIndex={0} 
                        aria-label={t("actionsGroupResponsible", {login: responsible.login})}
                        role='group'
                        className='responsible-table__container_actions_group'
                    >
                        {sessionUser.id === responsible.id ? (
                            <>
                                {' '}
                            </>
                        ) : text.active === "Y" ? (
                            <>
                                <Tooltip placement="topRight" title={t('edit')}>
                                    <EditOutlined
                                        style={{ fontSize: 20, color: '#4a4a4a' }}
                                        onClick={() => edit(responsible)}
                                        aria-label={t('editResponsible')}
                                        tabIndex={0}
                                        role='button'
                                    />
                                </Tooltip>
                                
                                <Tooltip placement="topRight" title={t('disable')}>
                                    <CloseCircleOutlined
                                        style={{ fontSize: 20, color: '#e9163c' }}
                                        onClick={() => patchResponsible(responsible.id, 'N')}
                                        aria-label={t('disableResponsible')}
                                        tabIndex={0}
                                        role='button'
                                    />
                                </Tooltip>
                            </>
                        ) : (
                            <Tooltip placement="topRight" title={t('enable')}>
                                <LoginOutlined
                                    style={{ fontSize: 20, color: '#27ae60' }}
                                    onClick={() => patchResponsible(responsible.id, 'Y')}
                                    aria-label={t('enableResponsible')}
                                    tabIndex={0}
                                    role='button'
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="responsible-table__container">
            <Table
                scroll={{
                    x: 800,
                }}
                rowKey="id"
                onRow={(item) => ({
                    onMouseLeave: () => onLeave(),
                    onMouseMove: () => onMove(item.id),
                })}
                pagination={{
                    showSizeChanger: true,
                    defaultPageSize: 50,
                }}
                rowClassName={(item) =>
                    classnames({
                        'hovered-row': hoverKey === item.id,
                        'is-matched': item.match,
                        'expanded-row': expandedRows.includes(item.id),
                    })
                }
                columns={columnsConfig}
                dataSource={data}
            />
        </div>
    );
}

ResponsibleTable.defaultProps = {
    data: [],
    patchResponsible: noop, 
    edit: noop
};

export default ResponsibleTable;
